import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock, AlertTriangle, CalendarDays, CalendarRange, History, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { crmService } from '@/lib/api';
import { TaskRow } from '@/components/crm/CrmTasks';

const SCOPES = [
  { id: 'overdue', label: 'Vencidas', icon: AlertTriangle, cls: 'text-red-600' },
  { id: 'today', label: 'Hoy', icon: CalendarDays, cls: 'text-sky-600' },
  { id: 'upcoming', label: 'Próximas', icon: CalendarRange, cls: 'text-slate-600' },
  { id: 'done', label: 'Historial', icon: History, cls: 'text-slate-400' },
];

const AgendaTab = ({ onOpenDebtor, focusTaskId, onFocusHandled }) => {
  const [scope, setScope] = useState('today');
  const [mine, setMine] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({ overdue: 0, today: 0, upcoming: 0, done: 0 });
  const [loading, setLoading] = useState(false);
  const [highlightId, setHighlightId] = useState(null);

  const load = useCallback(async (s = scope, m = mine) => {
    setLoading(true);
    try {
      const res = await crmService.tasks({ scope: s, mine: m });
      setTasks(res.data.tasks || []); setCounts(res.data.counts || {});
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [scope, mine]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!focusTaskId) return;
    (async () => {
      const res = await crmService.tasks({ scope: 'all' });
      const t = (res.data.tasks || []).find((x) => x.id === focusTaskId);
      if (t) {
        const now = new Date(); const at = new Date(t.scheduled_at);
        const s = t.status !== 'pending' ? 'done' : at < new Date(now.getFullYear(), now.getMonth(), now.getDate()) ? 'overdue' : at >= new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1) ? 'upcoming' : 'today';
        setMine(false); setScope(s); setHighlightId(focusTaskId);
        setTimeout(() => { document.getElementById(`crm-task-${focusTaskId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); onFocusHandled?.(); }, 600);
        setTimeout(() => setHighlightId(null), 6000);
      } else onFocusHandled?.();
    })();
  }, [focusTaskId, onFocusHandled]);

  return (
    <div className="space-y-4" data-testid="agenda-tab">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-teal-600" />Agenda de gestiones</h2>
        <label className="ml-auto flex items-center gap-2 text-sm text-slate-600 cursor-pointer"><Switch checked={mine} onCheckedChange={setMine} data-testid="agenda-mine-switch" />Solo mis llamadas</label>
        <Button variant="outline" size="sm" onClick={() => load()} data-testid="agenda-refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {SCOPES.map((s) => {
          const Icon = s.icon; const active = scope === s.id; const n = counts[s.id];
          return (
            <button key={s.id} type="button" onClick={() => setScope(s.id)} data-testid={`agenda-scope-${s.id}`}
              className={`rounded-xl border p-3 text-left transition-colors ${active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white hover:border-slate-400'}`}>
              <div className="flex items-center gap-2 text-xs font-medium"><Icon className={`w-4 h-4 ${active ? 'text-white' : s.cls}`} />{s.label}</div>
              {n != null && <p className={`text-2xl font-bold mt-1 ${active ? 'text-white' : 'text-slate-900'}`} data-testid={`agenda-count-${s.id}`}>{n}</p>}
            </button>
          );
        })}
      </div>
      <Card className="bg-white border-slate-200 p-4">
        {tasks.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8" data-testid="agenda-empty">No hay llamadas {SCOPES.find((s) => s.id === scope)?.label.toLowerCase()}{mine ? ' asignadas a vos' : ''}.</p>
        ) : (
          <div className="space-y-2">{tasks.map((t) => <TaskRow key={t.id} task={t} onChanged={() => load()} showDebtor onOpenDebtor={onOpenDebtor} highlighted={t.id === highlightId} />)}</div>
        )}
      </Card>
    </div>
  );
};

export default AgendaTab;
